import { FC, useEffect } from 'react';
import AppSidebar from './components/app-sidebar';
import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { useDocumentTitle } from 'usehooks-ts';
import AppHeader from './components/app-header';
import useApp from './hooks/use-app.hook';
import useFeature from './hooks/use-feature.hook';
import ModalRoot from './contexts/modal/modal-root';
import { useIsFetching } from 'react-query';
import usePrior from './hooks/use-prior.hook';
import AssumeRoleInlineToast from './components/toasts/assume-role/inline';
import useAppData from './hooks/use-app-data.hook';
import { AppDataQueryKey } from './lib/query-client';
import { UserControlProvider } from './contexts/user-control';
import { IntercomProvider } from './contexts/intercom';
import AppContent from './components/app-content';
import ChartsProvider from './components/charts-provider';
import { OrgBasicInfo } from './lib/models/org';
import DrawerRoot from './contexts/drawer/drawer-root';
import { DrawerProvider } from './contexts/drawer';
import useUserSettings from './hooks/use-user-settings.hook';

const SIDEBAR_WIDTH = 240;

const useStyles = createUseStyles<string, { signedIn: boolean }>((theme: Theme) => ({
  main: {
    flexGrow: 1,
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    backgroundColor: ({ signedIn }) =>
      signedIn ? theme.palette.background.app : theme.palette.common.white,
  },
  '@global': {
    body: {
      overflowY: 'hidden',
      color: theme.palette.common.black,
    },
    input: {
      '&:-webkit-autofill': {
        background: 'none',
        backgroundColor: 'transparent !important',
        backgroundClip: 'text',
      },
    },
    a: {
      color: theme.palette.link.main,
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.link.hover,
      },
      '&.flat': {
        color: theme.palette.common.black,
      },
    },
    '& .notistack-SnackbarContainer': {
      '& .notistack-MuiContent.notistack-MuiContent-info': {
        backgroundColor: theme.palette.info.main,
      },
      '& .notistack-MuiContent.notistack-MuiContent-warning': {
        backgroundColor: theme.palette.warning.main,
      },
    },
  },
}));

const App: FC = () => {
  const { signedIn, pageTitle, setPageLoadState, user } = useApp();
  const { sidebarOpen: open, toggleSidebar } = useUserSettings();
  const { hasAssume } = useFeature();

  const { plain } = pageTitle;
  const hasPageTitle = !!plain;
  useDocumentTitle(`F5 Workforce AI Security ${hasPageTitle ? '| ' : ''} ${plain}`);

  const { data: orgBasicInfo } = useAppData<OrgBasicInfo>(AppDataQueryKey.OrgBasicInfo, {
    enabled: signedIn,
  });
  const { role } = user || {};

  const styles = useStyles({ signedIn });
  const isFetching = useIsFetching();
  const priorIsFetching = usePrior(isFetching);

  useEffect(() => {
    if (isFetching && !priorIsFetching) {
      setPageLoadState('loading');
      return;
    }

    if (!isFetching && priorIsFetching) {
      setTimeout(() => {
        setPageLoadState('unloaded');
      }, 300);
      return;
    }
  }, [isFetching, priorIsFetching, setPageLoadState]);

  return (
    <UserControlProvider>
      <IntercomProvider>
        <ChartsProvider>
          <DrawerProvider>
            {hasAssume && <AssumeRoleInlineToast role={role!} orgName={orgBasicInfo?.name || ''} />}
            <Stack direction="row">
              {signedIn && (
                <AppSidebar
                  open={open}
                  onSidebarToggle={toggleSidebar}
                  width={SIDEBAR_WIDTH}
                  hasAssumeBanner={hasAssume}
                />
              )}
              <Stack
                component="main"
                className={styles.main}
                alignItems={signedIn ? 'flex-start' : 'center'}
              >
                <AppHeader signedIn={signedIn} />

                <AppContent hasAssumeBanner={hasAssume} />
              </Stack>
            </Stack>
            <ModalRoot />
            <DrawerRoot />
          </DrawerProvider>
        </ChartsProvider>
      </IntercomProvider>
    </UserControlProvider>
  );
};

export default App;
